/**
 * The gear at the top right, and the menu it opens (1.5.16 §7), in this order: Language · Appearance · Text size ·
 * My settings · Admin portal (administrators only) · Help & Support · Sign out. Nothing in it goes to Admin except
 * "Admin portal". It opens and closes with the button and with Escape; focus goes in, and back to the gear.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icons';
import { SettingsControls } from '@/components/SettingsControls';
import { SIGN_OUT_URL, type Me } from '@/auth/useMe';
import { clearTodayCopy } from '@/lib/today-cache';
import { HELP_LINK } from './nav';

export function GearMenu({ me }: { me: Me }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    pop.current?.querySelector<HTMLElement>('button, a')?.focus();
    const onDown = (e: MouseEvent) => {
      if (!pop.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="gear-wrap">
      <button
        ref={btn}
        className="iconbtn gear"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="settings-pop"
        aria-label={t('settings.open')}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="gear" />
      </button>
      {open ? (
        <div ref={pop} className="settings-pop" id="settings-pop" role="dialog" aria-label={t('settings.title')}>
          <SettingsControls gear />
          <div className="divider" />
          <div className="chips gear-links">
            {/* the person's own page (/settings) — never Admin */}
            <Link className="btn secondary sm" to="/settings" onClick={() => setOpen(false)} data-testid="pop-my-settings">
              {t('gear.mySettings')}
            </Link>
            {/* the administrator only (a co-administrator has no Admin console); /admin opens their last Admin page */}
            {me.role === 'admin' ? (
              <Link className="btn secondary sm" to="/admin" onClick={() => setOpen(false)} data-testid="pop-admin">
                {t('gear.adminPortal')}
              </Link>
            ) : null}
            {/* 1.6.2: it says what the page is called (it read "Staff guide"), for every role */}
            <Link className="btn secondary sm" to={HELP_LINK.to} onClick={() => setOpen(false)} data-testid="pop-help">
              <Icon name={HELP_LINK.icon} />
              {t(HELP_LINK.titleKey)}
            </Link>
            <a className="btn secondary sm" href={SIGN_OUT_URL} onClick={clearTodayCopy} data-testid="pop-sign-out">
              {t('settings.signOut')}
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}
